// src/pages/MigrationPage.tsx
import React, { useState } from 'react';
import { runCompleteMigration, verifyMigration } from '../utils/migrationUtility';

interface MigrationResult {
  success: boolean;
  usersUpdated: number;
  videosUpdated: number;
  sharesUpdated: number;
  errors: string[];
}

const MigrationPage: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<{
    users: MigrationResult;
    videos: MigrationResult;
    shares: MigrationResult;
  } | null>(null);
  const [verification, setVerification] = useState<{
    usersOk: boolean;
    videosOk: boolean;
    details: string[];
  } | null>(null);

  const handleRunMigration = async () => {
    if (!window.confirm(
      '⚠️ This will add sharing fields to all users and videos.\n\n' +
      'This is safe to run and won\'t delete any data.\n\n' +
      'Continue?'
    )) {
      return;
    }

    setIsRunning(true);
    setResult(null);
    setVerification(null);

    try {
      console.log('🚀 Starting migration...');
      const migrationResult = await runCompleteMigration();
      setResult(migrationResult);

      // Automatically verify after migration
      console.log('🔍 Verifying migration...');
      const verifyResult = await verifyMigration();
      setVerification(verifyResult);

      if (migrationResult.users.success && migrationResult.videos.success && migrationResult.shares.success) {
        alert(
          '✅ Migration Successful!\n\n' +
          `Users updated: ${migrationResult.users.usersUpdated}\n` +
          `Videos updated: ${migrationResult.videos.videosUpdated}\n` +
          `Shares updated: ${migrationResult.shares.sharesUpdated}\n\n` +
          'Phase 1 & Phase 2 database schemas are now complete!'
        );
      } else {
        alert(
          '⚠️ Migration completed with some errors.\n' +
          'Check the console and results below for details.'
        );
      }
    } catch (error) {
      console.error('❌ Migration failed:', error);
      alert(
        '❌ Migration Failed\n\n' +
        `Error: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
        'Check the console for details.'
      );
    } finally {
      setIsRunning(false);
    }
  };

  const handleVerifyOnly = async () => {
    setIsRunning(true);
    try {
      console.log('🔍 Verifying migration status...');
      const verifyResult = await verifyMigration();
      setVerification(verifyResult);

      if (verifyResult.usersOk && verifyResult.videosOk) {
        alert('✅ Verification Passed!\n\nAll users and videos have sharing fields.');
      } else {
        alert(
          '⚠️ Verification Issues Found\n\n' +
          verifyResult.details.join('\n') +
          '\n\nYou may need to run the migration.'
        );
      }
    } catch (error) {
      console.error('❌ Verification failed:', error);
      alert('❌ Verification failed. Check console for details.');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div style={{
      maxWidth: '900px',
      margin: '40px auto',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '30px',
        borderRadius: '12px',
        marginBottom: '30px',
        border: '2px solid #dee2e6'
      }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#212529' }}>
          🗄️ Phase 1 Database Migration
        </h1>
        <p style={{ margin: 0, color: '#6c757d', fontSize: '16px' }}>
          Add sharing fields to existing users and videos
        </p>
      </div>

      {/* Instructions */}
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#856404' }}>
          ℹ️ Before You Start
        </h3>
        <ul style={{ margin: '10px 0', paddingLeft: '20px', color: '#856404' }}>
          <li>This migration is <strong>safe</strong> and won't delete any data</li>
          <li>It will add sharing fields to all users and videos</li>
          <li>Safe to run multiple times (skips already-migrated documents)</li>
          <li>Takes 1-10 minutes depending on database size</li>
          <li>You should be signed in as an admin/owner</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '30px'
      }}>
        <button
          onClick={handleRunMigration}
          disabled={isRunning}
          style={{
            flex: 1,
            padding: '15px 30px',
            backgroundColor: isRunning ? '#6c757d' : '#FF6B00',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isRunning ? '⏳ Running Migration...' : '🚀 Run Migration'}
        </button>

        <button
          onClick={handleVerifyOnly}
          disabled={isRunning}
          style={{
            flex: 1,
            padding: '15px 30px',
            backgroundColor: isRunning ? '#6c757d' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isRunning ? '⏳ Checking...' : '🔍 Verify Migration'}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #dee2e6',
          borderRadius: '12px',
          padding: '25px',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#212529' }}>
            📊 Migration Results
          </h2>

          {/* Users */}
          <div style={{
            backgroundColor: result.users.success ? '#d4edda' : '#f8d7da',
            border: `1px solid ${result.users.success ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px'
          }}>
            <h3 style={{ 
              margin: '0 0 10px 0',
              color: result.users.success ? '#155724' : '#721c24'
            }}>
              {result.users.success ? '✅' : '❌'} Users Collection
            </h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Updated:</strong> {result.users.usersUpdated} users
            </p>
            {result.users.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <strong>Errors:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {result.users.errors.map((error, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#721c24' }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Videos */}
          <div style={{
            backgroundColor: result.videos.success ? '#d4edda' : '#f8d7da',
            border: `1px solid ${result.videos.success ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px'
          }}>
            <h3 style={{ 
              margin: '0 0 10px 0',
              color: result.videos.success ? '#155724' : '#721c24'
            }}>
              {result.videos.success ? '✅' : '❌'} Videos Collection
            </h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Updated:</strong> {result.videos.videosUpdated} videos
            </p>
            {result.videos.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <strong>Errors:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {result.videos.errors.map((error, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#721c24' }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Shares */}
          <div style={{
            backgroundColor: result.shares.success ? '#d4edda' : '#f8d7da',
            border: `1px solid ${result.shares.success ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px',
            padding: '15px'
          }}>
            <h3 style={{ 
              margin: '0 0 10px 0',
              color: result.shares.success ? '#155724' : '#721c24'
            }}>
              {result.shares.success ? '✅' : '❌'} Shares Collection
            </h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Updated:</strong> {result.shares.sharesUpdated} shares
            </p>
            {result.shares.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <strong>Errors:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  {result.shares.errors.map((error, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#721c24' }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Results */}
      {verification && (
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #dee2e6',
          borderRadius: '12px',
          padding: '25px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#212529' }}>
            🔍 Verification Results
          </h2>

          <div style={{
            backgroundColor: (verification.usersOk && verification.videosOk) ? '#d4edda' : '#fff3cd',
            border: `1px solid ${(verification.usersOk && verification.videosOk) ? '#c3e6cb' : '#ffc107'}`,
            borderRadius: '8px',
            padding: '15px'
          }}>
            <h3 style={{ margin: '0 0 10px 0' }}>
              {verification.usersOk && verification.videosOk ? '✅' : '⚠️'} Status
            </h3>
            <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
              {verification.details.map((detail, i) => (
                <li key={i} style={{ fontSize: '14px', marginBottom: '5px' }}>
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#6c757d'
      }}>
        <strong>💡 Next Steps:</strong>
        <ol style={{ margin: '10px 0', paddingLeft: '20px' }}>
          <li>Run the migration (if not done yet)</li>
          <li>Verify the migration passed</li>
          <li>Check Firebase Console to see the new fields</li>
          <li>Replace sharedSessionService.ts with Phase 2 version</li>
          <li>Proceed to Phase 3 implementation</li>
        </ol>
      </div>
    </div>
  );
};

export default MigrationPage;